import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import type { StoryCategory } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookText, Plus, Upload, Pencil, Archive, Loader2, Search, RefreshCw, ImageOff } from "lucide-react";

// ---- helpers -------------------------------------------------------------

function arrayToLines(a?: string[] | null): string {
  return (a ?? []).join("\n");
}
function linesToArray(s: string): string[] {
  return s.split("\n").map((x) => x.trim()).filter(Boolean);
}

type FormState = {
  name: string;
  slug: string;
  version: string;
  status: string;
  familyTags: string;
  description: string;
  coreStoryMethod: string;
  storyQuestion: string;
  storyMovement: string;
  viewerRealization: string;
  persuasionGoal: string;
  persuasionTags: string;
  emotionArc: string;
  typicalArc: string;
  metaphorBank: string;
  scenePatterns: string;
  motionVerbs: string;
  visualSymbols: string;
  visualsToAvoid: string;
  commonFailureModes: string;
  ctaLandingStyle: string;
  ctaBridgeExamples: string;
  exampleConcepts: string;
  badExamples: string;
  editNotes: string;
};

const EMPTY_FORM: FormState = {
  name: "", slug: "", version: "2.1-copy-reviewed", status: "active",
  familyTags: "", description: "", coreStoryMethod: "", storyQuestion: "",
  storyMovement: "", viewerRealization: "", persuasionGoal: "", persuasionTags: "",
  emotionArc: "", typicalArc: "", metaphorBank: "", scenePatterns: "",
  motionVerbs: "", visualSymbols: "", visualsToAvoid: "", commonFailureModes: "",
  ctaLandingStyle: "", ctaBridgeExamples: "", exampleConcepts: "[]", badExamples: "[]",
  editNotes: "",
};

function categoryToForm(c: StoryCategory): FormState {
  return {
    name: c.name ?? "",
    slug: c.slug ?? "",
    version: c.version ?? "",
    status: c.status ?? "active",
    familyTags: arrayToLines(c.familyTags),
    description: c.description ?? "",
    coreStoryMethod: c.coreStoryMethod ?? "",
    storyQuestion: c.storyQuestion ?? "",
    storyMovement: c.storyMovement ?? "",
    viewerRealization: c.viewerRealization ?? "",
    persuasionGoal: c.persuasionGoal ?? "",
    persuasionTags: arrayToLines(c.persuasionTags),
    emotionArc: arrayToLines(c.emotionArc),
    typicalArc: arrayToLines(c.typicalArc),
    metaphorBank: arrayToLines(c.metaphorBank),
    scenePatterns: arrayToLines(c.scenePatterns),
    motionVerbs: arrayToLines(c.motionVerbs),
    visualSymbols: arrayToLines(c.visualSymbols),
    visualsToAvoid: arrayToLines(c.visualsToAvoid),
    commonFailureModes: arrayToLines(c.commonFailureModes),
    ctaLandingStyle: c.ctaLandingStyle ?? "",
    ctaBridgeExamples: arrayToLines(c.ctaBridgeExamples),
    exampleConcepts: JSON.stringify(c.exampleConcepts ?? [], null, 2),
    badExamples: JSON.stringify(c.badExamples ?? [], null, 2),
    editNotes: c.editNotes ?? "",
  };
}

// Build the camelCase payload sent to the API. Throws on invalid JSON in the
// structured fields so callers can surface a clear error.
function formToPayload(f: FormState): Record<string, unknown> {
  let exampleConcepts: unknown = [];
  let badExamples: unknown = [];
  try {
    exampleConcepts = f.exampleConcepts.trim() ? JSON.parse(f.exampleConcepts) : [];
  } catch {
    throw new Error("Example Concepts is not valid JSON");
  }
  try {
    badExamples = f.badExamples.trim() ? JSON.parse(f.badExamples) : [];
  } catch {
    throw new Error("Bad Examples is not valid JSON");
  }
  return {
    name: f.name.trim(),
    slug: f.slug.trim() || f.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""),
    version: f.version.trim() || null,
    status: f.status.trim() || "active",
    familyTags: linesToArray(f.familyTags),
    description: f.description.trim() || null,
    coreStoryMethod: f.coreStoryMethod.trim() || null,
    storyQuestion: f.storyQuestion.trim() || null,
    storyMovement: f.storyMovement.trim() || null,
    viewerRealization: f.viewerRealization.trim() || null,
    persuasionGoal: f.persuasionGoal.trim() || null,
    persuasionTags: linesToArray(f.persuasionTags),
    emotionArc: linesToArray(f.emotionArc),
    typicalArc: linesToArray(f.typicalArc),
    metaphorBank: linesToArray(f.metaphorBank),
    scenePatterns: linesToArray(f.scenePatterns),
    motionVerbs: linesToArray(f.motionVerbs),
    visualSymbols: linesToArray(f.visualSymbols),
    visualsToAvoid: linesToArray(f.visualsToAvoid),
    commonFailureModes: linesToArray(f.commonFailureModes),
    ctaLandingStyle: f.ctaLandingStyle.trim() || null,
    ctaBridgeExamples: linesToArray(f.ctaBridgeExamples),
    exampleConcepts,
    badExamples,
    editNotes: f.editNotes.trim() || null,
  };
}

// Normalize a pasted JSON object (snake_case from the PDF/seed format OR
// camelCase) into the camelCase API contract for mass import.
function mapJsonToStoryCategory(data: Record<string, any>): Record<string, unknown> {
  const pick = (snake: string, camel: string) => data[camel] ?? data[snake];
  const rawBad = pick("bad_examples", "badExamples");
  const badExamples = Array.isArray(rawBad)
    ? rawBad.map((b: any) => ({
        bad_example: b?.bad_example ?? b?.badExample ?? "",
        why_bad: b?.why_bad ?? b?.whyBad ?? "",
      }))
    : undefined;
  const out: Record<string, unknown> = {
    name: data.name,
    slug: data.slug,
    version: data.version,
    status: data.status,
    familyTags: pick("family_tags", "familyTags"),
    description: data.description,
    imageUrl: pick("image_url", "imageUrl"),
    coreStoryMethod: pick("core_story_method", "coreStoryMethod"),
    storyQuestion: pick("story_question", "storyQuestion"),
    storyMovement: pick("story_movement", "storyMovement"),
    viewerRealization: pick("viewer_realization", "viewerRealization"),
    persuasionGoal: pick("persuasion_goal", "persuasionGoal"),
    persuasionTags: pick("persuasion_tags", "persuasionTags"),
    emotionArc: pick("emotion_arc", "emotionArc"),
    typicalArc: pick("typical_arc", "typicalArc"),
    metaphorBank: pick("metaphor_bank", "metaphorBank"),
    scenePatterns: pick("scene_patterns", "scenePatterns"),
    motionVerbs: pick("motion_verbs", "motionVerbs"),
    visualSymbols: pick("visual_symbols", "visualSymbols"),
    visualsToAvoid: pick("visuals_to_avoid", "visualsToAvoid"),
    commonFailureModes: pick("common_failure_modes", "commonFailureModes"),
    ctaLandingStyle: pick("cta_landing_style", "ctaLandingStyle"),
    ctaBridgeExamples: pick("cta_bridge_examples", "ctaBridgeExamples"),
    exampleConcepts: pick("example_concepts", "exampleConcepts"),
    badExamples,
    editNotes: pick("edit_notes", "editNotes"),
  };
  for (const k of Object.keys(out)) {
    if (out[k] === undefined) delete out[k];
  }
  return out;
}

// ---- field components ----------------------------------------------------

function TextField({ id, label, value, onChange, placeholder }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} />
    </div>
  );
}

function AreaField({ id, label, value, onChange, hint, rows = 3 }: {
  id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; rows?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Textarea id={id} value={value} rows={rows} className="font-normal"
        onChange={(e) => onChange(e.target.value)} data-testid={`textarea-${id}`} />
    </div>
  );
}

function ChipList({ items }: { items?: string[] | null }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <Badge key={i} variant="secondary" className="font-normal">{it}</Badge>
      ))}
    </div>
  );
}

// ---- manager ---------------------------------------------------------------
// Embeddable Story Categories management surface. Rendered inside the Admin
// dashboard (Story & Video Styles → Story Categories) — no page-level layout wrapper.

export default function StoryCategoriesManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<StoryCategory | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [importOpen, setImportOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<StoryCategory | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;

  const { data: categories, isLoading } = useQuery<StoryCategory[]>({
    queryKey: ["/api/story-categories"],
  });

  const upd = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (!payload.name) throw new Error("Name is required");
      if (editing) {
        return apiRequest("PATCH", `/api/story-categories/${editing.id}`, payload);
      }
      return apiRequest("POST", "/api/story-categories", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/story-categories"] });
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      toast({ title: editing ? "Story category updated" : "Story category created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(jsonText);
      const list = Array.isArray(parsed) ? parsed : [parsed];
      const mapped = list.map(mapJsonToStoryCategory);
      return apiRequest("POST", "/api/story-categories/import", mapped);
    },
    onSuccess: async (res: any) => {
      const data = await res.json().catch(() => ({}));
      queryClient.invalidateQueries({ queryKey: ["/api/story-categories"] });
      setImportOpen(false);
      setJsonText("");
      toast({
        title: "Import complete",
        description: `${data.created ?? 0} imported${data.skipped ? `, ${data.skipped} skipped` : ""}.`,
      });
    },
    onError: (e: Error) => toast({ title: "Import failed", description: e.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/story-categories/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/story-categories"] });
      setArchiveTarget(null);
      toast({ title: "Story category archived" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Regenerate the on-brand 500x500 graphic for one category (master/superuser).
  const [graphicBusyId, setGraphicBusyId] = useState<string | null>(null);
  const regenerateGraphicMutation = useMutation({
    mutationFn: async (id: string) =>
      apiRequest("POST", `/api/story-categories/${id}/regenerate-graphic`),
    onMutate: (id: string) => setGraphicBusyId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/story-categories"] });
      toast({ title: "Graphic regenerated" });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't regenerate graphic", description: e.message, variant: "destructive" }),
    onSettled: () => setGraphicBusyId(null),
  });

  // Replace the graphic with a user-uploaded image: upload to durable storage
  // first (returns a stable URL), then PATCH imageUrl onto the record.
  const replaceGraphicMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch("/api/upload", { method: "POST", body: fd, credentials: "include" });
      if (!up.ok) throw new Error("Upload failed");
      const { url } = await up.json();
      if (!url) throw new Error("Upload did not return a URL");
      return apiRequest("PATCH", `/api/story-categories/${id}`, { imageUrl: url });
    },
    onMutate: ({ id }) => setGraphicBusyId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/story-categories"] });
      toast({ title: "Graphic replaced" });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't replace graphic", description: e.message, variant: "destructive" }),
    onSettled: () => setGraphicBusyId(null),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (c: StoryCategory) => {
    setEditing(c);
    setForm(categoryToForm(c));
    setDialogOpen(true);
  };
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-2">
        <BookText className="h-10 w-10 mx-auto text-muted-foreground" />
        <h1 className="text-lg font-semibold">Story Categories</h1>
        <p className="text-sm text-muted-foreground">This library is available to master and superuser accounts only.</p>
      </div>
    );
  }

  const families = [...new Set((categories ?? []).flatMap((c) => c.familyTags ?? []))].sort();
  const filtered = (categories ?? []).filter((c) => {
    if (familyFilter !== "all" && !(c.familyTags ?? []).includes(familyFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.name.toLowerCase().includes(q) &&
          !(c.description ?? "").toLowerCase().includes(q) &&
          !(c.slug ?? "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by family tag for display. Multi-family categories appear under each
  // of their family tags (not just the first), so a category tagged with two
  // families shows up under both groups.
  const grouped = new Map<string, StoryCategory[]>();
  for (const c of filtered) {
    const keys = (c.familyTags ?? []).length ? c.familyTags! : ["Uncategorized"];
    for (const key of keys) {
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(c);
    }
  }
  const groupKeys = [...grouped.keys()].sort();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <BookText className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Story Categories</h1>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Global library of narrator-led video-ad storytelling templates. Each category stores
            story DNA — method, persuasion logic, visual language, and examples.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="button-open-import">
            <Upload className="h-4 w-4 mr-2" /> Import JSON
          </Button>
          <Button onClick={openCreate} data-testid="button-new-category">
            <Plus className="h-4 w-4 mr-2" /> New category
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..." className="pl-9" data-testid="input-search" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant={familyFilter === "all" ? "default" : "outline"}
            onClick={() => setFamilyFilter("all")} data-testid="filter-family-all">All</Button>
          {families.map((fam) => (
            <Button key={fam} size="sm" variant={familyFilter === fam ? "default" : "outline"}
              onClick={() => setFamilyFilter(fam)} data-testid={`filter-family-${fam}`}>{fam}</Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="text-empty">
          No story categories yet. Create one or import the library JSON.
        </div>
      ) : (
        <div className="space-y-8">
          {groupKeys.map((fam) => (
            <div key={fam} className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {fam} <span className="text-xs font-normal">({grouped.get(fam)!.length})</span>
              </h2>
              <div className="grid gap-3">
                {grouped.get(fam)!.map((c) => {
                  const isOpen = expanded.has(c.id);
                  return (
                    <Card key={c.id} data-testid={`card-category-${c.id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <StoryCategoryGraphic
                              category={c}
                              busy={graphicBusyId === c.id}
                              onRegenerate={() => regenerateGraphicMutation.mutate(c.id)}
                              onReplace={(file) => replaceGraphicMutation.mutate({ id: c.id, file })}
                            />
                            <div className="space-y-1 min-w-0">
                              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                {c.name}
                                {c.version && <Badge variant="outline" className="font-normal text-xs">{c.version}</Badge>}
                              </CardTitle>
                              <p className="text-xs text-muted-foreground font-mono">{c.slug}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(c)}
                              data-testid={`button-edit-${c.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setArchiveTarget(c)}
                              data-testid={`button-archive-${c.id}`}>
                              <Archive className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        {c.description && <p className="text-muted-foreground">{c.description}</p>}
                        <div className="flex flex-wrap gap-1.5">
                          {(c.familyTags ?? []).map((t, i) => (
                            <Badge key={i} className="font-normal">{t}</Badge>
                          ))}
                        </div>
                        <Button variant="ghost" size="sm" className="px-2 h-auto py-1 text-primary"
                          onClick={() => toggleExpand(c.id)} data-testid={`button-expand-${c.id}`}>
                          {isOpen ? "Hide details" : "Show details"}
                        </Button>
                        {isOpen && (
                          <div className="space-y-3 pt-2 border-t">
                            {c.coreStoryMethod && (
                              <Detail label="Core story method" text={c.coreStoryMethod} />
                            )}
                            {c.storyQuestion && <Detail label="Story question" text={c.storyQuestion} />}
                            {c.storyMovement && <Detail label="Story movement" text={c.storyMovement} />}
                            {c.viewerRealization && <Detail label="Viewer realization" text={c.viewerRealization} />}
                            {c.persuasionGoal && <Detail label="Persuasion goal" text={c.persuasionGoal} />}
                            <DetailChips label="Persuasion tags" items={c.persuasionTags} />
                            <DetailChips label="Emotion arc" items={c.emotionArc} />
                            <DetailList label="Typical arc" items={c.typicalArc} />
                            <DetailChips label="Metaphor bank" items={c.metaphorBank} />
                            <DetailList label="Scene patterns" items={c.scenePatterns} />
                            <DetailChips label="Motion verbs" items={c.motionVerbs} />
                            <DetailChips label="Visual symbols" items={c.visualSymbols} />
                            <DetailList label="Visuals to avoid" items={c.visualsToAvoid} />
                            <DetailList label="Common failure modes" items={c.commonFailureModes} />
                            {c.ctaLandingStyle && <Detail label="CTA landing" text={c.ctaLandingStyle} />}
                            <DetailList label="CTA bridge examples" items={c.ctaBridgeExamples} />
                            {c.exampleConcepts && c.exampleConcepts.length > 0 && (
                              <div className="space-y-1">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Example concepts</p>
                                <div className="space-y-1.5">
                                  {c.exampleConcepts.map((ex, i) => (
                                    <div key={i} className="text-sm">
                                      <span className="font-medium">{ex.industry}: </span>
                                      <span className="text-muted-foreground">{ex.concept}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {c.badExamples && c.badExamples.length > 0 && (
                              <div className="space-y-1">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bad examples</p>
                                <div className="space-y-1.5">
                                  {c.badExamples.map((ex, i) => (
                                    <div key={i} className="text-sm">
                                      <span className="text-muted-foreground">{ex.bad_example}</span>
                                      <span className="text-destructive"> — {ex.why_bad}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {c.editNotes && <Detail label="Edit notes" text={c.editNotes} />}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit story category" : "New story category"}</DialogTitle>
            <DialogDescription>
              List fields take one item per line. Example/Bad examples are JSON arrays.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <TextField id="name" label="Name" value={form.name} onChange={(v) => upd({ name: v })} />
              <TextField id="slug" label="Slug" value={form.slug} onChange={(v) => upd({ slug: v })}
                placeholder="auto from name if blank" />
              <TextField id="version" label="Version" value={form.version} onChange={(v) => upd({ version: v })} />
              <TextField id="status" label="Status" value={form.status} onChange={(v) => upd({ status: v })} />
            </div>
            <AreaField id="familyTags" label="Family tags" value={form.familyTags}
              onChange={(v) => upd({ familyTags: v })} hint="One per line" rows={2} />
            <AreaField id="description" label="Description" value={form.description} onChange={(v) => upd({ description: v })} />
            <AreaField id="coreStoryMethod" label="Core story method" value={form.coreStoryMethod} onChange={(v) => upd({ coreStoryMethod: v })} />
            <AreaField id="storyQuestion" label="Story question" value={form.storyQuestion} onChange={(v) => upd({ storyQuestion: v })} rows={2} />
            <AreaField id="storyMovement" label="Story movement" value={form.storyMovement} onChange={(v) => upd({ storyMovement: v })} rows={2} />
            <AreaField id="viewerRealization" label="Viewer realization" value={form.viewerRealization} onChange={(v) => upd({ viewerRealization: v })} />
            <AreaField id="persuasionGoal" label="Persuasion goal" value={form.persuasionGoal} onChange={(v) => upd({ persuasionGoal: v })} rows={2} />
            <AreaField id="persuasionTags" label="Persuasion tags" value={form.persuasionTags} onChange={(v) => upd({ persuasionTags: v })} hint="One per line" />
            <AreaField id="emotionArc" label="Emotion arc" value={form.emotionArc} onChange={(v) => upd({ emotionArc: v })} hint="One per line" />
            <AreaField id="typicalArc" label="Typical arc" value={form.typicalArc} onChange={(v) => upd({ typicalArc: v })} hint="One per line" />
            <AreaField id="metaphorBank" label="Metaphor bank" value={form.metaphorBank} onChange={(v) => upd({ metaphorBank: v })} hint="One per line" />
            <AreaField id="scenePatterns" label="Scene patterns" value={form.scenePatterns} onChange={(v) => upd({ scenePatterns: v })} hint="One per line" />
            <AreaField id="motionVerbs" label="Motion verbs" value={form.motionVerbs} onChange={(v) => upd({ motionVerbs: v })} hint="One per line" />
            <AreaField id="visualSymbols" label="Visual symbols" value={form.visualSymbols} onChange={(v) => upd({ visualSymbols: v })} hint="One per line" />
            <AreaField id="visualsToAvoid" label="Visuals to avoid" value={form.visualsToAvoid} onChange={(v) => upd({ visualsToAvoid: v })} hint="One per line" />
            <AreaField id="commonFailureModes" label="Common failure modes" value={form.commonFailureModes} onChange={(v) => upd({ commonFailureModes: v })} hint="One per line" />
            <AreaField id="ctaLandingStyle" label="CTA landing style" value={form.ctaLandingStyle} onChange={(v) => upd({ ctaLandingStyle: v })} />
            <AreaField id="ctaBridgeExamples" label="CTA bridge examples" value={form.ctaBridgeExamples} onChange={(v) => upd({ ctaBridgeExamples: v })} hint="One per line" />
            <AreaField id="exampleConcepts" label="Example concepts (JSON)" value={form.exampleConcepts} onChange={(v) => upd({ exampleConcepts: v })}
              hint='Array of { "industry": "...", "concept": "..." }' rows={5} />
            <AreaField id="badExamples" label="Bad examples (JSON)" value={form.badExamples} onChange={(v) => upd({ badExamples: v })}
              hint='Array of { "bad_example": "...", "why_bad": "..." }' rows={5} />
            <AreaField id="editNotes" label="Edit notes" value={form.editNotes} onChange={(v) => upd({ editNotes: v })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel">Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import story categories</DialogTitle>
            <DialogDescription>
              Paste a single category object or an array of categories. Keys may be snake_case
              (as in the library export) or camelCase.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} rows={16}
            className="font-mono text-xs" placeholder='[ { "name": "...", "slug": "..." } ]'
            data-testid="textarea-import-json" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="button-import-cancel">Cancel</Button>
            <Button onClick={() => importMutation.mutate()} disabled={importMutation.isPending || !jsonText.trim()}
              data-testid="button-import-submit">
              {importMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirm */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive story category?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.name}" will be hidden from the library. This can be restored later by an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              data-testid="button-archive-confirm">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// The on-brand 500x500 graphic for a story category, with master-only
// regenerate (AI) and replace (upload) controls overlaid on hover.
function StoryCategoryGraphic({
  category,
  busy,
  onRegenerate,
  onReplace,
}: {
  category: StoryCategory;
  busy: boolean;
  onRegenerate: () => void;
  onReplace: (file: File) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-muted"
      data-testid={`graphic-category-${category.id}`}
    >
      {category.imageUrl ? (
        <img
          src={category.imageUrl}
          alt={`${category.name} graphic`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
          <ImageOff className="h-5 w-5" />
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/55 opacity-0 transition-opacity group-hover:opacity-100">
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-white" />
        ) : (
          <>
            <button
              type="button"
              title="Regenerate graphic"
              onClick={onRegenerate}
              className="rounded p-1 text-white hover:bg-white/20"
              data-testid={`button-regenerate-graphic-${category.id}`}
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Upload replacement"
              onClick={() => fileRef.current?.click()}
              className="rounded p-1 text-white hover:bg-white/20"
              data-testid={`button-replace-graphic-${category.id}`}
            >
              <Upload className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`input-replace-graphic-${category.id}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onReplace(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function Detail({ label, text }: { label: string; text: string }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm">{text}</p>
    </div>
  );
}
function DetailList({ label, items }: { label: string; items?: string[] | null }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <ul className="list-disc pl-5 text-sm space-y-0.5">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
function DetailChips({ label, items }: { label: string; items?: string[] | null }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <ChipList items={items} />
    </div>
  );
}
